import { patch } from "$root/utils";
import { ViewModeEnum, type ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import { unreachable } from "@mdotm/utils/runtime";
import type { ColumnState } from "ag-grid-community";
import { Map as ImmutableMap } from "immutable";
import { chartTypeSortingMode } from "../chart-type";
import type { FilterWorkspaceChain } from "../chain";
import type { ParameterizedConceptDTOWithMandatoryUuid, Workspace, WorkspaceChartCtx } from "../ctx/context";
// One chain → concept translation, shared rather than restated here: it was one of six copies of the same
// twelve assignments, and a second copy is exactly the bug this MR is about.
import { workspaceChainToConceptDto } from "../concept";
import type { PersistedConceptFields } from "../spec-gaps";
import { type ActionOf, type WorkspaceAction, isEphemeralAction } from "./action";
import type { WorkspaceEffect } from "./effect";
import { assertWorkspaceInvariants } from "./invariants";
import {
	type ConceptPool,
	type WorkspaceState,
	addPrivateConcept,
	addSharedConcept,
	findConcept,
	patchPrivateConcept,
	privateConcepts,
	removeConcept,
	sharedConcepts,
} from "./state";

/**
 * The `conceptId` of a custom-attribute concept the backend has not assigned one to yet.
 *
 * `addColumn {property}` is the one operation whose ids are not all client-minted: the concept's uuid is,
 * but its `conceptId` names a concept *definition* the backend creates as a side effect of creating the
 * custom attribute. The reducer therefore holds a placeholder so the column can exist immediately, and
 * `conceptReconciled` swaps it for the real one when the effect lands.
 */
export const pendingCustomAttributeConceptId = "customAttributeId: pending";

/**
 * Everything the reducer needs that is not state or action. Only id minting, and only because a
 * client-minted id is what makes the whole design local-first — see `WorkspaceEffect`.
 *
 * Injected so the reducer is a *function* in the property tests (a seeded generator makes a sequence
 * reproducible) rather than something that has to be reasoned about around `crypto.randomUUID`.
 */
export type ReducerDeps = { uuid(): string };

const defaultDeps: ReducerDeps = { uuid: () => crypto.randomUUID() };

export type ReduceResult = {
	state: WorkspaceState;
	/** What the action asks of the world outside the workspace document, in the order it asks. */
	effects: WorkspaceEffect[];
};

/** A single action's contribution: the state it produced, and anything it owes the outside world. */
type Transition = { state: WorkspaceState; effects?: WorkspaceEffect[] };

/**
 * `(state, action) => state`. Pure — id minting aside, which {@link ReducerDeps} makes explicit.
 *
 * Three things happen on the way out, in this order, and none of them is the caller's to remember:
 *
 * 1. the transition itself;
 * 2. the **sweep** — the mark-and-sweep view code used to call by hand from JSX, after every content action
 *    rather than after the six that happened to remember;
 * 3. the invariants, which throw ({@link assertWorkspaceInvariants}).
 *
 * Both 2 and 3 are skipped for an ephemeral action, which reaches neither `concepts` nor `content` and so
 * can neither orphan a concept nor break a reference. That is what keeps the check off the per-keystroke
 * path; see the note on `assertWorkspaceInvariants`.
 *
 * Invalid input throws, with today's messages verbatim: the controllers reject a chain with no concept id
 * or no name rather than writing a broken concept, and the panels rely on the rejection. A throwing
 * reducer leaves state untouched, which is the same outcome as today.
 */
export function reduce(state: WorkspaceState, action: WorkspaceAction, deps: ReducerDeps = defaultDeps): ReduceResult {
	const transition = apply(state, action, deps);
	if (isEphemeralAction(action)) {
		return { state: transition.state, effects: transition.effects ?? [] };
	}

	const swept = sweepUnreferencedPrivateConcepts(transition.state);
	assertWorkspaceInvariants(swept, `after ${action.type}`);

	return { state: swept, effects: transition.effects ?? [] };
}

function apply(state: WorkspaceState, action: WorkspaceAction, deps: ReducerDeps): Transition {
	switch (action.type) {
		case "addColumn":
			return addColumn(state, action, deps);
		case "editColumn":
			return editColumn(state, action, deps);
		case "renameTagOption":
			return renameTagOption(state, action);
		case "tagOptionRenamed":
			return tagOptionRenamed(state, action);
		case "removeColumn":
			return removeColumn(state, action);
		case "reorderColumns":
			return { state: withContent(state, { columns: action.columns }) };
		case "addFilter":
			return addFilter(state, action, deps);
		case "upsertFilter":
			return upsertFilter(state, action, deps);
		case "editFilter":
			return editFilter(state, action.parameterizedConceptUuid, action.chain);
		case "removeFilter":
			return removeFilter(state, action);
		case "removeAllFilters":
			return { state: withContent(state, { filters: [] }) };
		case "addChart":
			return addChart(state, action, deps);
		case "editChart":
			return editChart(state, action, deps);
		case "removeChart":
			return removeChart(state, action);
		case "reorderCharts":
			return { state: withContent(state, { charts: action.charts }) };
		case "setView":
			return setView(state, action);
		case "setSort":
			return { state: withContent(state, { sort: action.sort }) };
		case "setPageSize":
			return { state: withContent(state, { pageSize: action.size }) };
		case "sharedConceptsReplaced":
			return { state: replaceSharedConcepts(state, action.concepts) };
		case "conceptReconciled":
			return { state: reconcileConcept(state, action.placeholderUuid, action.concept) };
		// Whole, not merged field by field: the incoming document is authoritative for everything read off
		// `meta` — see the note on the action — and content is not read off it at all.
		case "documentRefreshed":
			return { state: { ...state, meta: { ...state.meta, document: action.document } } };
		case "setSearch":
			return { state: withUi(state, { search: action.search }) };
		case "setSelection":
			return { state: withUi(state, { selection: action.selection }) };
		case "setPage":
			return { state: withUi(state, { page: action.page }) };
		case "setResultTotal":
			return { state: withUi(state, { resultTotal: action.total }) };
		default:
			return unreachable(action);
	}
}

function withContent(state: WorkspaceState, content: Partial<WorkspaceState["content"]>): WorkspaceState {
	return { ...state, content: { ...state.content, ...content } };
}

function withUi(state: WorkspaceState, ui: Partial<WorkspaceState["ui"]>): WorkspaceState {
	return { ...state, ui: { ...state.ui, ...ui } };
}

// --- Columns -------------------------------------------------------------------------------------

/** A new column is visible and last. `columns` is `null` until a layout exists, so the first one starts it. */
function appendColumn(columns: Workspace["columns"], colId: string): ColumnState[] {
	return [...(columns ?? []), { colId, hide: false }];
}

function addColumn(state: WorkspaceState, action: ActionOf<"addColumn">, deps: ReducerDeps): Transition {
	if ("property" in action) {
		const concept: ParameterizedConceptDTOWithMandatoryUuid = {
			uuid: deps.uuid(),
			conceptId: pendingCustomAttributeConceptId,
			label: action.property.name,
			source: "CURRENT",
			representationMode: "ABSOLUTE",
		};

		return {
			state: {
				...state,
				concepts: addSharedConcept(state.concepts, concept),
				content: { ...state.content, columns: appendColumn(state.content.columns, concept.uuid) },
			},
			effects: [{ effect: "createCustomAttributeConcept", property: action.property }],
		};
	}

	assertChain(action.chain, { name: "missing concept name" });
	const concept: ParameterizedConceptDTOWithMandatoryUuid = {
		uuid: deps.uuid(),
		...workspaceChainToConceptDto(action.chain),
	};

	// Where the concept lands is the only difference between the two, and it follows from the payload.
	return {
		state: {
			...state,
			concepts: action.share ? addSharedConcept(state.concepts, concept) : addPrivateConcept(state.concepts, concept),
			content: { ...state.content, columns: appendColumn(state.content.columns, concept.uuid) },
		},
		effects: action.share ? [{ effect: "createSharedConcept", concept }] : [],
	};
}

function editColumn(state: WorkspaceState, action: ActionOf<"editColumn">, deps: ReducerDeps): Transition {
	if ("property" in action) {
		return {
			state,
			effects: [
				{
					effect: "updateCustomAttributeConcept",
					parameterizedConceptUuid: action.parameterizedConceptUuid,
					property: action.property,
				},
			],
		};
	}

	assertChain(action.chain, { name: "missing name" });

	if (action.shared) {
		// A shared concept keeps its uuid, so there is nothing to repoint and no cached row to invalidate —
		// hence no local change at all. The pool catches up through `sharedConceptsReplaced`, which is what
		// the API's response carries today.
		return {
			state,
			effects: [
				{
					effect: "updateSharedConcept",
					parameterizedConceptUuid: action.parameterizedConceptUuid,
					concept: { uuid: action.parameterizedConceptUuid, ...workspaceChainToConceptDto(action.chain) },
				},
			],
		};
	}

	const previous = privateConcepts(state.concepts).find(({ uuid }) => uuid === action.parameterizedConceptUuid);
	if (!previous) {
		throw new Error("missing parametrized concept");
	}

	// Editing a private column *substitutes* its concept rather than updating it in place: the new
	// parameterization gets a new uuid, so grid rows cached against the old one cannot be shown under it.
	// The substitute is merged onto the existing column state, so width, pinning and order survive.
	const substitute: ParameterizedConceptDTOWithMandatoryUuid = {
		uuid: deps.uuid(),
		...workspaceChainToConceptDto(action.chain),
	};

	return {
		state: {
			...state,
			concepts: addPrivateConcept(state.concepts, substitute),
			content: {
				...state.content,
				columns: repointColumn(state.content.columns, previous.uuid, substitute.uuid),
			},
		},
	};
}

function repointColumn(columns: Workspace["columns"], from: string, to: string): Workspace["columns"] {
	if (!columns) {
		return columns;
	}
	const index = columns.findIndex((column) => column.colId === from);
	if (index === -1) {
		return columns;
	}
	const next = columns.slice();
	next[index] = { ...next[index], colId: to, hide: false };
	return next;
}

function removeColumn(state: WorkspaceState, action: ActionOf<"removeColumn">): Transition {
	const { parameterizedConceptUuid } = action;
	const dropColumn = (columns: Workspace["columns"]) =>
		columns?.filter((column) => column.colId !== parameterizedConceptUuid) ?? null;

	if (action.shared) {
		// Resolved here, before the drop, and sent *with* the effect. The effect's implementation cannot look
		// it up afterwards — this branch is what removes it from the pool — and it needs the whole entity for
		// the request body and its `conceptId` for the managed-concept guard. Same shape as
		// `createSharedConcept`, and the same message the service used to fail with when it could not resolve.
		const concept = sharedConcepts(state.concepts).find(({ uuid }) => uuid === parameterizedConceptUuid);
		if (!concept) {
			throw new Error(`No shared concept found for uuid: ${parameterizedConceptUuid}`);
		}

		// Deliberately no existence check on the column: the shared path deletes the concept and then
		// filters a list that may not contain it. Pinned in `column.test.ts`.
		//
		// The concept itself is deleted tenant-wide by the API, so everything pointing at it goes with it —
		// see `dropConceptAndReferences`. Before, the filter or chart series that named it was left dangling
		// and every reader downstream had to guard.
		return {
			state: dropConceptAndReferences(state, parameterizedConceptUuid),
			effects: [{ effect: "deleteSharedConcept", concept }],
		};
	}

	if (!state.content.columns?.some((column) => column.colId === parameterizedConceptUuid)) {
		throw new Error(`no column matching ${parameterizedConceptUuid}`);
	}

	return { state: withContent(state, { columns: dropColumn(state.content.columns) }) };
}

/**
 * Ask for the rename, and leave local state alone until it has committed.
 *
 * The grid's data request is keyed on the filters, so a criterion rewritten here would be asked of a
 * backend that still holds the old value. {@link tagOptionRenamed} is the rewrite, dispatched by the
 * effect once the transaction has answered.
 */
function renameTagOption(state: WorkspaceState, action: ActionOf<"renameTagOption">): Transition {
	return {
		state,
		effects: [
			{
				effect: "renameCustomAttributeTagOption",
				parameterizedConceptUuid: action.parameterizedConceptUuid,
				currentTagValue: action.currentTagValue,
				newTagValue: action.newTagValue,
			},
		],
	};
}

/**
 * Rewrite a renamed tag option wherever this client is holding the old value.
 *
 * Filters are matched by `conceptId`, not by the uuid the rename was asked for. A filter mints its own
 * parameterized concept ({@link addFilter}), so the filter on an attribute and the column on that same
 * attribute do not share a uuid; what they share is the concept they point at.
 */
function tagOptionRenamed(state: WorkspaceState, action: ActionOf<"tagOptionRenamed">): Transition {
	const renamed = findConcept(state.concepts, action.parameterizedConceptUuid);
	if (!renamed?.concept.conceptId) {
		return { state };
	}

	const filters = state.content.filters.map((filter) => {
		const concept = findConcept(state.concepts, filter.parameterizedConceptUuid);
		if (concept?.concept.conceptId !== renamed.concept.conceptId) {
			return filter;
		}

		return { ...filter, arguments: renameInArguments(filter.arguments, action) };
	});

	return { state: withContent(state, { filters }) };
}

/**
 * Swap the renamed value inside one filter's arguments, leaving every other value in place.
 *
 * A tag criterion stores its values as one nested array — `[["Tag1", "Tag2"]]` — so a portfolio carrying
 * `Tag1` and `Tag2` keeps the second when the first is renamed. Anything that is not a string is passed
 * through: `arguments` is `Array<object>` and the other operators put dates and numbers in it.
 */
function renameInArguments(args: object[], action: ActionOf<"tagOptionRenamed">): object[] {
	const swap = (value: unknown) => (value === action.currentTagValue ? action.newTagValue : value);

	return args.map((argument) =>
		Array.isArray(argument) ? (argument.map(swap) as unknown as object) : (swap(argument) as object),
	);
}

// --- Filters -------------------------------------------------------------------------------------

function filterArguments(chain: FilterWorkspaceChain): object[] {
	return (chain.comparisonValue ?? []) as object[];
}

function addFilter(state: WorkspaceState, action: ActionOf<"addFilter">, deps: ReducerDeps): Transition {
	assertChain(action.chain, { name: "missing concept name" });
	if (!action.chain.comparisonMode) {
		throw new Error("missing concept comparisonMode");
	}

	// A filter on a concept that already exists — the shared one the panel resolved — points straight at it
	// and mints nothing. It used to be passed as a bare uuid and stored nothing, which is how a filter could
	// end up referencing a concept the pool did not hold; now the concept comes with it, and the pool takes
	// it if this is the first the workspace has heard of it. Shared, because that is where the caller found
	// it, and because the sweep must not collect it.
	const concept: ParameterizedConceptDTOWithMandatoryUuid = action.concept ?? {
		uuid: deps.uuid(),
		...workspaceChainToConceptDto(action.chain),
	};
	const concepts = findConcept(state.concepts, concept.uuid)
		? state.concepts
		: action.concept
			? addSharedConcept(state.concepts, concept)
			: addPrivateConcept(state.concepts, concept);

	return {
		state: {
			...state,
			concepts,
			content: {
				...state.content,
				filters: [
					...state.content.filters,
					{
						parameterizedConceptUuid: concept.uuid,
						keyword: action.chain.comparisonMode,
						arguments: filterArguments(action.chain),
					},
				],
			},
		},
	};
}

/**
 * "Filter by these values" from the table's tag cell: the one caller that genuinely means *either*.
 *
 * It matches on state, not on intent — an existing `in` filter over the same concept definition has its
 * values replaced, anything else appends. Note what the match then does: the edit is rebuilt from the
 * *matched concept*, so the incoming chain contributes only `comparisonValue`. The incoming name is
 * dropped and `relativeness` is cleared (the concept spread carries `relativeness`, the chain reads
 * `parameterizedRelativeness`). Both are pinned in `filter.test.ts`; reproduced here rather than fixed,
 * because fixing them is a behaviour change and this commit is not the place for one.
 */
function upsertFilter(state: WorkspaceState, action: ActionOf<"upsertFilter">, deps: ReducerDeps): Transition {
	assertChain(action.chain, { name: "missing concept name" });
	if (!action.chain.comparisonMode) {
		throw new Error("missing concept comparisonMode");
	}

	const filterByUuid = ImmutableMap(
		state.content.filters.map((filter) => [filter.parameterizedConceptUuid, filter] as const),
	);
	const match = privateConcepts(state.concepts)
		.filter((concept) => filterByUuid.has(concept.uuid))
		.find((concept) => {
			const filter = filterByUuid.get(concept.uuid);
			return (
				action.chain.comparisonMode === filter?.keyword &&
				action.chain.comparisonMode === "in" &&
				action.chain.concept?.id === concept.conceptId
			);
		});

	if (!match) {
		return addFilter(state, { type: "addFilter", chain: action.chain }, deps);
	}

	return editFilter(state, match.uuid, {
		...match,
		name: match.label,
		concept: { id: match.conceptId },
		comparisonMode: filterByUuid.get(match.uuid)!.keyword,
		comparisonValue: action.chain.comparisonValue,
	});
}

function editFilter(state: WorkspaceState, parameterizedConceptUuid: string, chain: FilterWorkspaceChain): Transition {
	if (!chain.concept?.id) {
		throw new Error("missing conceptId");
	}
	if (!chain.comparisonMode) {
		throw new Error("missing comparisonMode");
	}
	if (!chain.name) {
		throw new Error("missing name");
	}

	const filters = state.content.filters.map((filter) =>
		filter.parameterizedConceptUuid === parameterizedConceptUuid
			? { ...filter, keyword: chain.comparisonMode!, arguments: filterArguments(chain) }
			: filter,
	);

	// Unlike a column edit this updates the concept in place — a filter has no cached rows keyed by it.
	// `source` is written verbatim, `undefined` included, which is where it differs from the chain → DTO
	// translation used everywhere else (that one defaults to `CURRENT`).
	return {
		state: {
			...state,
			concepts: patchPrivateConcept(state.concepts, parameterizedConceptUuid, {
				conceptId: chain.concept.id,
				label: chain.name,
				representationMode: chain.representationMode,
				relativeness: chain.parameterizedRelativeness,
				timeHorizon: chain.timeHorizon,
				parameters: chain.parameters as ParameterizedConceptDTO["parameters"],
				source: chain.source,
				confidenceLevel: chain.confidenceLevel,
			}),
			content: { ...state.content, filters },
		},
	};
}

function removeFilter(state: WorkspaceState, action: ActionOf<"removeFilter">): Transition {
	const { parameterizedConceptUuid } = action;
	if (!state.content.filters.some((filter) => filter.parameterizedConceptUuid === parameterizedConceptUuid)) {
		throw new Error(`no filter matching ${parameterizedConceptUuid}`);
	}

	return {
		state: withContent(state, {
			filters: state.content.filters.filter((filter) => filter.parameterizedConceptUuid !== parameterizedConceptUuid),
		}),
	};
}

// --- Charts --------------------------------------------------------------------------------------

/**
 * The fields of a series concept that are stored, and only those.
 *
 * `satisfies PersistedConceptFields` is what keeps `timeSeries` and `breakdown` out: they are request-time
 * flags, and the pool is what "Create Column" copies out of, so a concept in it has to keep meaning "one
 * number". A caller that passes them anyway must not get them persisted — pinned in `chart.test.ts`.
 */
function seriesConcept(concept: ParameterizedConceptDTO): Omit<ParameterizedConceptDTOWithMandatoryUuid, "uuid"> {
	return {
		conceptId: concept.conceptId,
		label: concept.label,
		timeHorizon: concept.timeHorizon,
		representationMode: concept.representationMode,
		relativeness: concept.relativeness,
		source: concept.source ?? "CURRENT",
		confidenceLevel: concept.confidenceLevel,
		parameters: concept.parameters,
	} satisfies PersistedConceptFields;
}

function addChart(state: WorkspaceState, action: ActionOf<"addChart">, deps: ReducerDeps): Transition {
	if (action.concepts.length === 0) {
		throw new Error("no concepts found, please provide it");
	}

	let concepts: ConceptPool = state.concepts;
	const series: string[] = [];
	for (const concept of action.concepts) {
		const minted: ParameterizedConceptDTOWithMandatoryUuid = { uuid: deps.uuid(), ...seriesConcept(concept) };
		concepts = addPrivateConcept(concepts, minted);
		series.push(minted.uuid);
	}

	const threshold = storeThresholdConcept(concepts, action.thresholdConcept, deps);

	return {
		state: {
			...state,
			concepts: threshold?.concepts ?? concepts,
			content: {
				...state.content,
				charts: [
					...state.content.charts,
					{
						...action.chart,
						...(threshold ? { relativeThreshold: threshold.uuid } : {}),
						series,
						preferences: action.chart.preferences ?? {},
					},
				],
			},
		},
	};
}

/**
 * Stores the key-metric threshold's concept, if the operation carries one.
 *
 * The threshold is a concept like any other — one number on the charted metric's scale — so it is stored in
 * the pool and referenced by uuid. Carrying it in the payload is what makes `chart.relativeThreshold` resolve
 * by construction; the panel mints it in a separate call today, and the reference between the two calls is
 * exactly the kind of gap this commit closes.
 */
function storeThresholdConcept(
	concepts: ConceptPool,
	threshold: ParameterizedConceptDTO | undefined,
	deps: ReducerDeps,
): { concepts: ConceptPool; uuid: string } | undefined {
	if (!threshold) {
		return undefined;
	}

	// A threshold concept keeps its `dataProvider`: unlike every other stored concept it is the threshold's
	// *mode* (manual, portfolio, reference) rather than a property of the charted metric.
	const stored: ParameterizedConceptDTOWithMandatoryUuid = {
		...threshold,
		uuid: threshold.uuid ?? deps.uuid(),
	};

	return {
		uuid: stored.uuid,
		concepts: findConcept(concepts, stored.uuid)
			? patchPrivateConcept(concepts, stored.uuid, stored)
			: addPrivateConcept(concepts, stored),
	};
}

function editChart(state: WorkspaceState, action: ActionOf<"editChart">, deps: ReducerDeps): Transition {
	const chart = chartAt(state, action.index);

	// A copy: the caller's options object is theirs, and today's controller mutates it.
	const options: Partial<WorkspaceChartCtx> = { ...action.chart };
	let concepts = state.concepts;

	// An empty `concepts` leaves the series alone — that is how the nine "just change a preference" call
	// sites work, and why this is not "replace the series with what I passed".
	if (action.concepts.length > 0) {
		const series: string[] = [];
		for (const concept of action.concepts) {
			if (concept.uuid) {
				if (findConcept(concepts, concept.uuid)) {
					// `source` is left out on purpose: it is what distinguishes one series of a chart from the next,
					// so an existing concept keeps the source it was created under.
					const { source: _source, ...rest } = seriesConcept(concept);
					concepts = patchPrivateConcept(concepts, concept.uuid, rest);
					series.push(concept.uuid);
					continue;
				}
				// A uuid the pool has never seen. The payload carries the whole concept, so it is stored under the
				// identity the caller gave it — where before the patch silently found nothing and the series was
				// left pointing at a concept that did not exist.
				concepts = addPrivateConcept(concepts, { ...seriesConcept(concept), uuid: concept.uuid });
				series.push(concept.uuid);
				continue;
			}
			const minted: ParameterizedConceptDTOWithMandatoryUuid = { uuid: deps.uuid(), ...seriesConcept(concept) };
			concepts = addPrivateConcept(concepts, minted);
			series.push(minted.uuid);
		}
		options.series = series;
	}

	const threshold = storeThresholdConcept(concepts, action.thresholdConcept, deps);
	if (threshold) {
		concepts = threshold.concepts;
		options.relativeThreshold = threshold.uuid;
	}

	const charts = state.content.charts.slice();
	charts[action.index] = clampChartSort(patch(chart, options));

	return { state: { ...state, concepts, content: { ...state.content, charts } } };
}

/**
 * Drops a sort direction the chart's type cannot apply.
 *
 * The reachable case is changing a sorted column chart into a line or a distribution: the type changes, the
 * preference stays, and the chart then owns a direction with nothing to order by. `sortingCleanUp` chases
 * the same inconsistency from a view effect, one chart at a time and only for the workspace sort; this is
 * the same rule where the change actually happens.
 */
function clampChartSort(chart: WorkspaceChartCtx): WorkspaceChartCtx {
	if (chart.preferences?.sort == null || chartTypeSortingMode(chart.chartType) !== "NONE") {
		return chart;
	}
	return { ...chart, preferences: { ...chart.preferences, sort: null } };
}

function removeChart(state: WorkspaceState, action: ActionOf<"removeChart">): Transition {
	chartAt(state, action.index);
	const charts = state.content.charts.slice();
	charts.splice(action.index, 1);
	return { state: withContent(state, { charts }) };
}

function chartAt(state: WorkspaceState, index: number): WorkspaceChartCtx {
	if (index < 0) {
		throw new Error("index not valid");
	}
	const chart = state.content.charts.at(index);
	if (chart === undefined) {
		throw new Error("Chart not found, please provide a valid index");
	}
	return chart;
}

// --- View ----------------------------------------------------------------------------------------

/**
 * Selecting a view has two consequences outside the workspace document, and both are declared rather
 * than performed: the table caps itself to the viewport and scrolls its own grid body, every other view
 * grows with its content. Re-selecting the current view is not short-circuited — it re-applies both.
 */
function setView(state: WorkspaceState, action: ActionOf<"setView">): Transition {
	return {
		state: withContent(state, { view: action.view }),
		effects: [
			{ effect: "scrollToTop" },
			{ effect: "setMaxPageHeight", maxPageHeight: action.view === ViewModeEnum.Table ? "screen" : "auto" },
		],
	};
}

// --- Post-conditions -----------------------------------------------------------------------------

/** Every uuid the workspace document points at, from all five places that can point at one. */
export function referencedConceptUuids(state: WorkspaceState): Set<string> {
	const referenced = new Set<string>();
	for (const filter of state.content.filters) {
		referenced.add(filter.parameterizedConceptUuid);
	}
	for (const chart of state.content.charts) {
		for (const uuid of chart.series) {
			referenced.add(uuid);
		}
		if (chart.relativeThreshold != null) {
			referenced.add(chart.relativeThreshold);
		}
	}
	for (const column of state.content.columns ?? []) {
		if (column.colId != null) {
			referenced.add(column.colId);
		}
	}
	if (state.content.sort?.parameterizedConceptUuid != null) {
		referenced.add(state.content.sort.parameterizedConceptUuid);
	}
	return referenced;
}

/**
 * The mark-and-sweep `deleteUnusedParametrizedConcepts` used to perform, as a post-condition of every content
 * action rather than a call from JSX.
 *
 * Shared concepts are exempt: they have their own API and outlive the workspace, so an unreferenced one
 * is not garbage — it is somebody else's column.
 */
export function sweepUnreferencedPrivateConcepts(state: WorkspaceState): WorkspaceState {
	const referenced = referencedConceptUuids(state);
	const concepts = state.concepts.filter((entry) => entry.origin === "STANDARD" || referenced.has(entry.concept.uuid));
	return concepts.length === state.concepts.length ? state : { ...state, concepts };
}

/**
 * A concept leaving the pool takes its references with it.
 *
 * The mirror image of the sweep: the sweep removes a concept nothing points at, this removes what points at
 * a concept that is gone. Reached from the two places a concept can disappear without the document asking —
 * a shared column deleted tenant-wide, and the shared pool coming back from its API without it.
 *
 * A chart that loses its last series goes too. That is the one visible consequence: deleting a shared
 * concept a chart plotted used to leave the chart in place rendering its "broken" state, and now the chart
 * is removed with the concept.
 */
function dropConceptAndReferences(state: WorkspaceState, uuid: string): WorkspaceState {
	const charts = state.content.charts.flatMap((chart): [WorkspaceChartCtx] | [] => {
		const series = chart.series.filter((seriesUuid) => seriesUuid !== uuid);
		if (series.length === 0) {
			return [];
		}
		return [
			{
				...chart,
				series,
				...(chart.relativeThreshold === uuid ? { relativeThreshold: undefined } : {}),
			},
		];
	});

	return {
		...state,
		concepts: removeConcept(state.concepts, uuid),
		content: {
			...state.content,
			columns: state.content.columns?.filter((column) => column.colId !== uuid) ?? null,
			filters: state.content.filters.filter((filter) => filter.parameterizedConceptUuid !== uuid),
			charts,
			sort: state.content.sort?.parameterizedConceptUuid === uuid ? null : state.content.sort,
		},
	};
}

/**
 * Adopts the shared pool as its API reports it. Anything it no longer carries has been deleted by somebody
 * — possibly another user of the tenant — so it is dropped with its references rather than left dangling.
 */
function replaceSharedConcepts(
	state: WorkspaceState,
	concepts: ParameterizedConceptDTOWithMandatoryUuid[],
): WorkspaceState {
	const stillThere = new Set(concepts.map((concept) => concept.uuid));
	const vanished = state.concepts.filter((entry) => entry.origin === "STANDARD" && !stillThere.has(entry.concept.uuid));

	const withoutVanished = vanished.reduce((acc, entry) => dropConceptAndReferences(acc, entry.concept.uuid), state);

	return {
		...withoutVanished,
		concepts: [
			...withoutVanished.concepts.filter((entry) => entry.origin === "WORKSPACE"),
			...concepts.map((concept) => ({ concept, origin: "STANDARD" as const })),
		],
	};
}

/**
 * Swaps a client-minted placeholder for the concept the backend accepted, repointing every reference to
 * it. The pool keeps the placeholder's position and origin, so column order does not move under the user.
 */
function reconcileConcept(
	state: WorkspaceState,
	placeholderUuid: string,
	concept: ParameterizedConceptDTOWithMandatoryUuid,
): WorkspaceState {
	if (placeholderUuid === concept.uuid) {
		return {
			...state,
			concepts: state.concepts.map((entry) => (entry.concept.uuid === placeholderUuid ? { ...entry, concept } : entry)),
		};
	}

	const repoint = (uuid: string) => (uuid === placeholderUuid ? concept.uuid : uuid);

	return {
		...state,
		concepts: state.concepts.map((entry) => (entry.concept.uuid === placeholderUuid ? { ...entry, concept } : entry)),
		content: {
			...state.content,
			columns:
				state.content.columns?.map((column) =>
					column.colId === placeholderUuid ? { ...column, colId: concept.uuid } : column,
				) ?? null,
			filters: state.content.filters.map((filter) => ({
				...filter,
				parameterizedConceptUuid: repoint(filter.parameterizedConceptUuid),
			})),
			charts: state.content.charts.map((chart) => ({
				...chart,
				series: chart.series.map(repoint),
				...(chart.relativeThreshold != null ? { relativeThreshold: repoint(chart.relativeThreshold) } : {}),
			})),
			sort:
				state.content.sort?.parameterizedConceptUuid === placeholderUuid
					? { ...state.content.sort, parameterizedConceptUuid: concept.uuid }
					: state.content.sort,
		},
	};
}

/**
 * The two things every chain-carrying operation rejects, with today's messages. The concept id and the
 * name are what `workspaceChainToConceptDto` dereferences, so a chain missing either would produce a
 * concept that renders as nothing; the name message is the one that differs between create and edit.
 */
function assertChain(chain: { concept?: { id?: string }; name?: string }, messages: { name: string }): void {
	if (!chain.concept?.id) {
		throw new Error("missing concept id");
	}
	if (!chain.name) {
		throw new Error(messages.name);
	}
}
